import React from 'react';
import { ColumnContainer } from '../OperationListView.styles';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { ColumnConfig } from 'src/components/stowage/operationListView/utils/columnConfig';
import CargoNumber from '../../common/cargoNumber';
import { PortName } from 'src/types/ViewModel/Stowage/Port/PortName';
import { getOperationType, getOptionString, getMarpolPollutionCategoryString, getIbcShipTypeString } from 'src/utils/stowage/cargoOptionUtils';
import { getChartererName, getRange } from 'src/utils/stowage/cargoOperationUtils';
import { getCorrectedVoyageTemperatureRange } from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import { getCargoOperationStowedTankNames } from 'src/utils/stowage/stowageOperation/stowageOperationUtils';
import { formatPortNamesAsString } from 'src/utils/stowage/stowageOperation/operationFormatUtils';
import {
    getLitreWeightValue,
    getMaxAdjacentTemperatureValue,
    getStowedWeight,
    getStowedVolume,
    getNotStowedWeightForOperationType,
    getNotStowedVolumeForOperationType,
    getBillOfLading,
    getNominatedWeightForCargoOperation,
    getNominatedVolumeForCargoOperation,
    getShipFigureValue,
    getTotalLoadingCargoContractWeightValue,
} from 'src/utils/stowage/operationListView/operationListItemValueGetters';
import { getTotalSwingWeights, getTotalSwingVolumes } from 'src/utils/stowage/operationListView/operationListItemUtils';

interface OperationDataColumnProps {
    column: ColumnConfig;
    cargoOperation: RotationCargoOperation;
    activeStowagePlan: StowagePlan;
    columnWidth: string | number;
    warningClass?: string;
    specialClass?: string;
}

/**
 * Utility function for formatting port names as React elements (JSX-specific)
 */
const formatPortNamesAsElements = (portNames: PortName[]): React.ReactNode => {
    if (!portNames?.length) return null;
    return portNames.map((port, index) => (
        <React.Fragment key={`port_${port.portId}_${index}`}>
            <span>{port.name}</span>
            {index < portNames.length - 1 && <span>, </span>}
        </React.Fragment>
    ));
};

/**
 * Renders a single data column for an operation list item.
 * Handles content rendering based on column type with proper formatting.
 */
const OperationDataColumn: React.FC<OperationDataColumnProps> = ({
    column,
    cargoOperation,
    activeStowagePlan,
    columnWidth,
    warningClass = '',
    specialClass = '',
}) => {
    // Build className with warning and special classes
    let className = 'col ' + (column.cssClass || '');
    if (warningClass) className += warningClass;
    if (specialClass) className += ` ${specialClass}`;

    // Get column content based on column id
    let content: React.ReactNode;
    switch (column.id) {
        case 'operationType':
            content = getOperationType(cargoOperation.operationType);
            break;
        case 'cargoNr':
            content = <CargoNumber includeColoredBackground cargo={cargoOperation.cargo} />;
            break;
        case 'tradeName':
            content = cargoOperation.cargo.product.tradeName;
            break;
        case 'productName':
            content = cargoOperation.cargo.product.category.name;
            break;
        case 'nominatedWeight':
            content = getNominatedWeightForCargoOperation(cargoOperation);
            break;
        case 'nominatedVolume':
            content = getNominatedVolumeForCargoOperation(activeStowagePlan, cargoOperation);
            break;
        case 'notStowedWeight':
            content = getNotStowedWeightForOperationType(activeStowagePlan, cargoOperation);
            break;
        case 'notStowedVolume':
            content = getNotStowedVolumeForOperationType(activeStowagePlan, cargoOperation);
            break;
        case 'stowedWeight':
            content = getStowedWeight(activeStowagePlan, cargoOperation);
            break;
        case 'stowedVolume':
            content = getStowedVolume(activeStowagePlan, cargoOperation);
            break;
        case 'charterer':
            content = getChartererName(cargoOperation.cargo);
            break;
        case 'contractWeight':
            content = cargoOperation.contractWeight;
            break;
        case 'totalCargoNominatedWeight':
            content = getTotalLoadingCargoContractWeightValue(activeStowagePlan, cargoOperation);
            break;
        case 'billOfLading':
            content = getBillOfLading(cargoOperation);
            break;
        case 'shipFigure':
            content = getShipFigureValue(activeStowagePlan, cargoOperation);
            break;
        case 'option':
            content = getOptionString(cargoOperation.cargo);
            break;
        case 'swingWeights':
            content = getTotalSwingWeights(activeStowagePlan, cargoOperation);
            break;
        case 'swingVolumes':
            content = getTotalSwingVolumes(activeStowagePlan, cargoOperation);
            break;
        case 'litreWeight':
            content = getLitreWeightValue(activeStowagePlan, cargoOperation.cargo);
            break;
        case 'heating':
            content = getCorrectedVoyageTemperatureRange(cargoOperation.cargo);
            break;
        case 'maxAdjacentTemp':
            content = getMaxAdjacentTemperatureValue(cargoOperation.cargo);
            break;
        case 'flashPoint':
            content = getRange(cargoOperation.cargo.product.flashPointRange.min, cargoOperation.cargo.product.flashPointRange.max);
            break;
        case 'meltingPoint':
            content = getRange(cargoOperation.cargo.product.meltingPointRange.min, cargoOperation.cargo.product.meltingPointRange.max);
            break;
        case 'boilingPoint':
            content = getRange(cargoOperation.cargo.product.boilingPointRange.min, cargoOperation.cargo.product.boilingPointRange.max);
            break;
        case 'tankNames':
            content = getCargoOperationStowedTankNames(activeStowagePlan, cargoOperation).toString();
            break;
        case 'ports':
            content = (
                <>
                    {formatPortNamesAsElements(cargoOperation.cargo.loadingPortNames)} /{' '}
                    {formatPortNamesAsElements(cargoOperation.cargo.unloadingPortNames)}
                </>
            );
            break;
        case 'uscgGroupNumber':
            content = cargoOperation.cargo.product.uscgGroup.number;
            break;
        case 'pollution':
            content = getMarpolPollutionCategoryString(cargoOperation.cargo.product.category.marpolPollutionCategory);
            break;
        case 'shipType':
            content = getIbcShipTypeString(cargoOperation.cargo.product.category?.shipType);
            break;
        case 'source':
            content = cargoOperation.dataSource;
            break;
        case 'contractStatus':
            content = cargoOperation.cargo.cargoStatus;
            break;
        default:
            content = '';
    }

    // Generate title attribute for certain columns (tooltip)
    let title: string | undefined;
    if (column.id === 'tankNames') {
        title = getCargoOperationStowedTankNames(activeStowagePlan, cargoOperation).toString();
    } else if (column.id === 'ports') {
        title = `${formatPortNamesAsString(cargoOperation.cargo.loadingPortNames)} / ${formatPortNamesAsString(
            cargoOperation.cargo.unloadingPortNames
        )}`;
    } else if (column.id === 'tradeName') {
        title = cargoOperation.cargo.product.tradeName;
    } else if (column.id === 'charterer') {
        title = getChartererName(cargoOperation.cargo);
    }

    return (
        <ColumnContainer
            key={column.id}
            className={className}
            width={typeof columnWidth === 'number' ? `${columnWidth}px` : columnWidth}
            title={title}
        >
            {content}
        </ColumnContainer>
    );
};

export default OperationDataColumn;
